// src/components/ui/DownloadDropdown.jsx

import React, { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getDownloadUrl, getAllDownloadUrls } from '@/services/apiService';

/**
 * DownloadDropdown
 *
 * Standalone download button visible to all users.
 * Shows a dropdown with:
 *   - Download current file (uses activeFile)
 *   - Download all (n files)
 *
 * Props:
 *   submissionId  (number)  required
 *   activeFile    (object)  { file_url, file_type } — the currently visible slide
 *   totalFiles    (number)  total file count for the label
 */

const triggerDownload = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const DownloadDropdown = ({ submissionId, activeFile, totalFiles = 1 }) => {
  const [downloadingCurrent, setDownloadingCurrent] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const handleDownloadCurrent = async () => {
    if (!activeFile?.file_url || downloadingCurrent) return;
    setDownloadingCurrent(true);
    try {
      const downloadUrl = await getDownloadUrl(submissionId, activeFile.file_url);
      triggerDownload(downloadUrl);
    } catch (err) {
      console.error('Download current failed:', err);
    } finally {
      setDownloadingCurrent(false);
    }
  };

  const handleDownloadAll = async () => {
    if (downloadingAll) return;
    setDownloadingAll(true);
    try {
      const files = await getAllDownloadUrls(submissionId);
      files.forEach(({ download_url }, index) => {
        setTimeout(() => triggerDownload(download_url), index * 300);
      });
      // Reset after all downloads have been initiated
      setTimeout(() => setDownloadingAll(false), files.length * 300 + 500);
    } catch (err) {
      console.error('Download all failed:', err);
      setDownloadingAll(false);
    }
  };

  const isBusy = downloadingCurrent || downloadingAll;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={isBusy}
          className="text-swan hover:text-white focus:outline-none disabled:opacity-50"
          title="Download"
        >
          {isBusy ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            <Download className="h-5 w-5" />
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="bg-stone-brown border border-field-grey text-swan"
      >
        <DropdownMenuItem
          onClick={handleDownloadCurrent}
          disabled={downloadingCurrent}
          className="cursor-pointer text-swan hover:bg-field-grey/20 disabled:opacity-50"
        >
          {downloadingCurrent ? 'Downloading…' : 'Download current file'}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleDownloadAll}
          disabled={downloadingAll}
          className="cursor-pointer text-swan hover:bg-field-grey/20 disabled:opacity-50"
        >
          {downloadingAll ? 'Downloading…' : `Download all (${totalFiles})`}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DownloadDropdown;